import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Users } from "lucide-react";
import { createCustomerSchema } from "@laundrypro/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorText,
  Input,
  PageHeader,
  SearchInput,
  SkeletonTableRows,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, money } from "../lib/format";

interface CustomerListItem {
  id: string;
  name: string;
  mobile: string | null;
  email: string | null;
  orderCount: number;
  outstandingMinor: number;
  totalSpentMinor: number;
  createdAt: string;
}

/**
 * Customers.
 *
 * The create form lives inline in the first card — the audited pattern. There is
 * no "New customer" button in the page header, and no separate create page: the
 * operator sees the form the moment they arrive, which is what makes taking a
 * walk-in fast.
 */
export function CustomersPage() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [items, setItems] = useState<CustomerListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Inline create form state.
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const canCreate = hasPermission("customers.create");

  const load = useCallback(
    async (term: string) => {
      setLoadError(null);
      try {
        const params = term ? `?search=${encodeURIComponent(term)}&pageSize=50` : "?pageSize=50";
        const data = await apiFetch<{ items: CustomerListItem[] }>(`/customers${params}`);
        setItems(data.items);
      } catch (err) {
        setItems([]);
        setLoadError(
          err instanceof ApiError ? err.message : ar ? "تعذّر تحميل العملاء." : "Couldn't load customers.",
        );
      }
    },
    [ar],
  );

  useEffect(() => {
    void load("");
  }, [load]);

  // Debounced server-side search: the backend matches name, mobile AND order
  // reference, which a client-side filter over one page of rows cannot do.
  useEffect(() => {
    const timer = setTimeout(() => void load(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search, load]);

  const addCustomer = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const parsed = createCustomerSchema.safeParse({ name, mobile: mobile || null });
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) errors[issue.path.join(".")] ??= issue.message;
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch("/customers", { method: "POST", body: parsed.data });
      showToast("success", ar ? "تمت إضافة العميل." : "Customer added.");
      setName("");
      setMobile("");
      await load(search.trim());
    } catch (err) {
      if (err instanceof ApiError && err.code === "mobile_already_registered") {
        setFormError(ar ? "رقم الجوال مسجّل لعميل آخر بالفعل." : err.message);
      } else if (err instanceof ApiError) {
        setFormError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const loading = items === null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "العملاء" : "Customers"}
        subtitle={ar ? "سجل العملاء وأرصدتهم وطلباتهم" : "Customer records, balances and their orders"}
      />

      {canCreate && (
        <Card>
          <CardHeader title={ar ? "إضافة عميل" : "Add a customer"} />
          <form onSubmit={addCustomer} className="flex flex-wrap items-start gap-3">
            <div className="min-w-[180px] flex-1">
              <Input
                label={ar ? "الاسم" : "Name"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={fieldErrors.name}
                required
              />
            </div>
            <div className="min-w-[180px] flex-1">
              <Input
                label={ar ? "رقم الجوال" : "Mobile"}
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                error={fieldErrors.mobile}
                placeholder="05xxxxxxxx"
                inputMode="tel"
              />
            </div>
            {/* h-11 matches the Input height, since Button md is h-10. */}
            <Button type="submit" icon={<UserPlus className="size-4" />} loading={submitting} className="h-11">
              {ar ? "إضافة" : "Add customer"}
            </Button>
          </form>
          {formError && (
            <div className="mt-3">
              <ErrorText>{formError}</ErrorText>
            </div>
          )}
        </Card>
      )}

      <Card padding="none">
        <div className="flex flex-wrap items-center gap-3 p-5 pb-0 sm:p-6 sm:pb-0">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={ar ? "ابحث بالاسم أو الجوال أو رقم الطلب" : "Search name, mobile or order number"}
          />
        </div>

        {loadError && (
          <div className="p-5 sm:p-6">
            <ErrorText>{loadError}</ErrorText>
          </div>
        )}

        {!loading && items.length === 0 && !loadError ? (
          <EmptyState
            icon={<Users className="size-6" />}
            title={ar ? "لا يوجد عملاء بعد" : "No customers yet"}
            description={
              search
                ? ar
                  ? "لا نتائج لهذا البحث."
                  : "Nothing matches that search."
                : ar
                  ? "أضف أول عميل من النموذج أعلاه."
                  : "Add your first customer from the form above."
            }
          />
        ) : (
          <div className="p-5 sm:p-6">
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "الاسم" : "Name"}</Th>
                  <Th>{ar ? "الجوال" : "Mobile"}</Th>
                  <Th>{ar ? "الطلبات" : "Orders"}</Th>
                  <Th>{ar ? "الرصيد المستحق" : "Outstanding"}</Th>
                  <Th>{ar ? "إجمالي التعامل" : "Total spent"}</Th>
                </tr>
              </THead>
              <tbody>
                {loading ? (
                  <SkeletonTableRows rows={5} cols={5} />
                ) : (
                  items.map((customer) => (
                    <Tr key={customer.id}>
                      <Td className="font-medium text-ink-900">
                        <Link to={`/customers/${customer.id}`} className="hover:text-accent-600 hover:underline">
                          {customer.name}
                        </Link>
                      </Td>
                      <Td className="tabular-nums text-ink-600">{customer.mobile ?? "-"}</Td>
                      <Td className="tabular-nums">{customer.orderCount}</Td>
                      <Td>
                        {customer.outstandingMinor > 0 ? (
                          <Badge tone="warning">
                            <span className="tabular-nums">
                              {money(customer.outstandingMinor)} {currency}
                            </span>
                          </Badge>
                        ) : (
                          <span className="tabular-nums text-muted-foreground">0.00 {currency}</span>
                        )}
                      </Td>
                      <Td className="tabular-nums font-medium">
                        {money(customer.totalSpentMinor)} {currency}
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
